import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API } from "../lib/api";

const AuthCtx = createContext(null);

export const STAFF_ROLES = ["SUPER_ADMIN", "ADMIN", "TAX_PROFESSIONAL", "ACCOUNT_MANAGER", "BOOKKEEPER", "SUPPORT"];
export const ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest
  const [business, setBusiness] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [activeBusinessId, setActiveBusinessId] = useState(localStorage.getItem("active_business_id") || "");
  const [impersonating, setImpersonating] = useState(!!sessionStorage.getItem("impersonation_token"));

  const reload = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      setUser(data.user);
      setBusiness(data.business);
      try {
        const list = await API.get("/my-businesses");
        setBusinesses(list.data.businesses);
        const active = localStorage.getItem("active_business_id");
        const stale = !list.data.businesses.some((b) => b.id === active);
        if (stale && list.data.businesses.length > 0) {
          localStorage.setItem("active_business_id", list.data.businesses[0].id);
          setActiveBusinessId(list.data.businesses[0].id);
        }
      } catch { setBusinesses([]); }
    } catch {
      setUser(false);
      setBusiness(null);
      setBusinesses([]);
    }
  }, []);

  const switchBusiness = async (id) => {
    localStorage.setItem("active_business_id", id);
    setActiveBusinessId(id);
    await reload();
  };

  useEffect(() => { reload(); }, [reload]);

  const login = async (email, password) => {
    sessionStorage.removeItem("impersonation_token");
    setImpersonating(false);
    const { data } = await API.post("/auth/login", { email, password });
    await reload();
    return data.user;
  };

  const logout = async () => {
    sessionStorage.removeItem("impersonation_token");
    try { await API.post("/auth/logout"); } catch {}
    setUser(false);
    setBusiness(null);
    setImpersonating(false);
  };

  const stopImpersonating = async () => {
    sessionStorage.removeItem("impersonation_token");
    setImpersonating(false);
    await reload();
  };

  return (
    <AuthCtx.Provider value={{ user, business, businesses, activeBusinessId, switchBusiness,
      impersonating, login, logout, reload, stopImpersonating }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

export function homeFor(user) {
  if (!user) return "/login";
  if (user.role === "TAX_PROFESSIONAL") return "/taxpro";
  if (STAFF_ROLES.includes(user.role)) return "/admin";
  return "/portal";
}
